'use client';

/**
 * SearchAiz Provider
 *
 * Root provider that combines all SearchAiz contexts:
 * - Settings (with multi-layer inheritance)
 * - Search state and actions
 * - Data source registry
 *
 * Usage:
 * ```tsx
 * <SearchAizProvider
 *   portalSettings={portalSettings}
 *   developerSettings={developerSettings}
 * >
 *   <SearchAiz mode="auto" context="header" />
 * </SearchAizProvider>
 * ```
 */

import {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
} from 'react';
import { useOptionalAccessControlState } from '@/components/shell/AppShellContext';
import { usePermissions } from '@/hooks/usePermissions';
import { SearchAizSettingsProvider } from '../hooks/useSearchAizSettings';
import type {
  PartialSettings,
  SearchContext,
  DataSourceDefinition,
} from '../types';
import {
  getAllDataSources,
  filterAccessibleSources,
} from '../utils/dataSourceRegistry';

// =============================================================================
// DATA SOURCE CONTEXT
// =============================================================================

interface DataSourceContextValue {
  /** All registered data sources */
  allSources: DataSourceDefinition[];
  /** Sources accessible to current user */
  accessibleSources: DataSourceDefinition[];
  /** Get source by ID */
  getSource: (id: string) => DataSourceDefinition | undefined;
}

const DataSourceContext = createContext<DataSourceContextValue | null>(null);

// =============================================================================
// PROVIDER
// =============================================================================

export interface SearchAizProviderProps {
  /** Portal-level settings (system admin) */
  portalSettings?: PartialSettings;
  /** Developer-level settings (app/integration) */
  developerSettings?: PartialSettings;
  /** Search context for settings resolution */
  context?: SearchContext;
  /** User's permissions (for data source access control) */
  userPermissions?: string[];
  /** User's billing tier (for data source access control) */
  billingTier?: 'free' | 'starter' | 'professional' | 'enterprise';
  /** Children to render */
  children: ReactNode;
}

export function SearchAizProvider({
  portalSettings = {},
  developerSettings = {},
  context = 'header',
  userPermissions,
  billingTier,
  children,
}: SearchAizProviderProps) {
  const { permissions } = usePermissions();
  const accessControlState = useOptionalAccessControlState();
  const resolvedPermissions = userPermissions ?? permissions;
  const accessControlTier =
    billingTier ??
    accessControlState?.userContext?.effective_tier ??
    'free';
  const resolvedBillingTier =
    accessControlTier === 'trial' ? 'free' : accessControlTier;

  // Get all and accessible data sources
  const dataSourceValue = useMemo<DataSourceContextValue>(() => {
    const allSources = getAllDataSources();

    // Get available source IDs from settings
    const availableIds =
      (developerSettings.sources?.available as string[]) ||
      (portalSettings.sources?.available as string[]) ||
      allSources.map((s) => s.id);

    // Filter to accessible sources based on permissions and billing
    const accessibleSources = filterAccessibleSources(
      availableIds,
      resolvedPermissions,
      resolvedBillingTier
    );

    return {
      allSources,
      accessibleSources,
        getSource: (id: string) => allSources.find((s) => s.id === id),
    };
  }, [portalSettings, developerSettings, resolvedPermissions, resolvedBillingTier]);

  return (
    <SearchAizSettingsProvider
      portalSettings={portalSettings}
      developerSettings={developerSettings}
      context={context}
    >
      <DataSourceContext.Provider value={dataSourceValue}>
        {children}
      </DataSourceContext.Provider>
    </SearchAizSettingsProvider>
  );
}

// =============================================================================
// HOOKS
// =============================================================================

/**
 * Hook to access data source registry
 */
export function useDataSources(): DataSourceContextValue {
  const context = useContext(DataSourceContext);

  if (!context) {
    // Return registry metadata only if not in provider; do not bypass access gates.
    const allSources = getAllDataSources();
    return {
      allSources,
      accessibleSources: [],
      getSource: (id: string) => allSources.find((s) => s.id === id),
    };
  }

  return context;
}

/**
 * Hook to get accessible source IDs
 */
export function useAccessibleSourceIds(): string[] {
  const { accessibleSources } = useDataSources();
  return useMemo(() => accessibleSources.map((s) => s.id), [accessibleSources]);
}

export default SearchAizProvider;
